import React from 'react'
import styles from './MicrositeHeroSectionForm.module.css'
import { Plus, Trash2, ChevronUp, ChevronDown } from 'lucide-react'
import TextInput from '../../../components/TextInput/TextInput'
import SelectInput from '../../../components/SelectInput/SelectInput'
import ToggleSwitch from '../../../components/ToggleSwitch/ToggleSwitch'
import MicrositeImageUploader from '../../../components/MicrositeImageUploader/MicrositeImageUploader'
import MicrositeTabSwitcher from '../../../components/MicrositeTabSwitcher/MicrositeTabSwitcher'
import MicrositeRepeatableRows from '../../../components/MicrositeRepeatableRows/MicrositeRepeatableRows'

// ──────────────────────────────────────────────────────────────
// Default skeleton for a brand-new hero section, matching the
// real production content shape exactly (activeMode/commonSettings/
// staticConfig/sliderConfig). A store with no saved hero content
// yet still gets sensible defaults to start editing from.
// ──────────────────────────────────────────────────────────────
const DEFAULT_CONTENT = {
    activeMode: 'STATIC',
    commonSettings: { mobileHidden: false },
    staticConfig: {
        layoutType: 'SPLIT_SCREEN',
        theme: { backgroundColor: '#fef2f2', textColor: '#1a1a1a', height: '100vh' },
        content: { heading: '', subHeading: '', description: '', alignment: 'left', actions: [] },
        media: { url: '', type: 'transparent_image', position: 'right', altText: '', floatingAnimation: true },
        widgets: []
    },
    sliderConfig: {
        settings: { autoPlay: true, interval: 5000, showArrows: true, showDots: true, height: '100vh' },
        slides: []
    }
}

const LAYOUT_TYPE_OPTIONS = [
    { value: 'SPLIT_SCREEN', label: 'Split screen' },
    { value: 'CENTERED', label: 'Centered' },
    { value: 'FULL_BLEED', label: 'Full bleed' }
]

const ALIGNMENT_OPTIONS = [
    { value: 'left', label: 'Left' },
    { value: 'center', label: 'Center' },
    { value: 'right', label: 'Right' }
]

const MEDIA_TYPE_OPTIONS = [
    { value: 'transparent_image', label: 'Transparent image (PNG cutout)' },
    { value: 'background_image', label: 'Background image' },
    { value: 'video', label: 'Video' }
]

const MEDIA_POSITION_OPTIONS = [
    { value: 'left', label: 'Left' },
    { value: 'right', label: 'Right' },
    { value: 'center', label: 'Center' }
]

const ACTION_TYPE_OPTIONS = [
    { value: 'primary_btn', label: 'Primary button' },
    { value: 'outline_btn', label: 'Outline button' },
    { value: 'text_link', label: 'Text link' }
]

const WIDGET_TYPE_OPTIONS = [
    { value: 'info_card', label: 'Info card' },
    { value: 'badge', label: 'Badge' },
    { value: 'rating_card', label: 'Rating card' }
]

const WIDGET_POSITION_OPTIONS = [
    { value: 'top-left', label: 'Top left' },
    { value: 'top-right', label: 'Top right' },
    { value: 'bottom-left', label: 'Bottom left' },
    { value: 'bottom-right', label: 'Bottom right' }
]

const EMPTY_ACTION = { label: '', url: '', type: 'primary_btn', icon: '' }
const EMPTY_WIDGET = { id: '', type: 'info_card', title: '', subtitle: '', position: 'bottom-left' }
const EMPTY_SLIDE = {
    id: '',
    content: { heading: '', subHeading: '', alignment: 'center', actions: [] },
    media: { url: '', type: 'background_image', overlayOpacity: 0.4 }
}

// Deep-merge helper so a partially-saved content object (e.g. from
// before this richer shape existed) still renders without crashing —
// any missing nested key falls back to DEFAULT_CONTENT's value.
const withDefaults = (content) => ({
    activeMode: content?.activeMode || DEFAULT_CONTENT.activeMode,
    commonSettings: { ...DEFAULT_CONTENT.commonSettings, ...(content?.commonSettings || {}) },
    staticConfig: {
        ...DEFAULT_CONTENT.staticConfig,
        ...(content?.staticConfig || {}),
        theme: { ...DEFAULT_CONTENT.staticConfig.theme, ...(content?.staticConfig?.theme || {}) },
        content: { ...DEFAULT_CONTENT.staticConfig.content, ...(content?.staticConfig?.content || {}) },
        media: { ...DEFAULT_CONTENT.staticConfig.media, ...(content?.staticConfig?.media || {}) },
        widgets: content?.staticConfig?.widgets || []
    },
    sliderConfig: {
        ...DEFAULT_CONTENT.sliderConfig,
        ...(content?.sliderConfig || {}),
        settings: { ...DEFAULT_CONTENT.sliderConfig.settings, ...(content?.sliderConfig?.settings || {}) },
        slides: content?.sliderConfig?.slides || []
    }
})

const genId = (prefix) => `${prefix}_${Math.random().toString(36).slice(2, 9)}`

/**
 * ActionListEditor
 * Repeatable editor for an `actions` array — shared shape between
 * staticConfig.content.actions and each slide's content.actions.
 */
const ActionListEditor = ({ actions, onChange }) => (
    <MicrositeRepeatableRows
        items={actions || []}
        onChange={onChange}
        emptyItem={{ ...EMPTY_ACTION }}
        addLabel="Add button / link"
        renderSummary={(item) => item.label || 'New action'}
        renderItem={(item, idx, updateItem) => (
            <div className={styles.nestedFields}>
                <div className={styles.row2}>
                    <TextInput
                        label="Label"
                        value={item.label}
                        onChange={(v) => updateItem('label', v)}
                        placeholder="e.g. Price List"
                    />
                    <TextInput
                        label="Link URL"
                        value={item.url}
                        onChange={(v) => updateItem('url', v)}
                        placeholder="/price-list"
                    />
                </div>
                <div className={styles.row2}>
                    <SelectInput
                        label="Button type"
                        value={item.type}
                        onChange={(v) => updateItem('type', v)}
                        options={ACTION_TYPE_OPTIONS}
                    />
                    <TextInput
                        label="Icon (optional)"
                        value={item.icon}
                        onChange={(v) => updateItem('icon', v)}
                        placeholder="e.g. phone_icon"
                    />
                </div>
            </div>
        )}
    />
)

/**
 * MicrositeHeroSectionForm
 * Content editor for the landing page hero banner section — the
 * richest of the 10 landing sections. Supports two render modes:
 *   - STATIC: a single split-screen/centered/full-bleed hero with
 *     its own theme, heading/subheading/description, action
 *     buttons, one media asset, and floating info-card widgets.
 *   - SLIDER: an auto-rotating carousel of slides, each with its
 *     own heading/actions/background image and overlay opacity.
 * `commonSettings.mobileHidden` applies regardless of mode.
 *
 * Pure presentational sub-form — receives `content` and `onChange`
 * from MicrositeLandingSection, which owns loading/saving/versioning.
 * No DB or API changes were required for this: osm_store_config_landing.content
 * is a generic JSON column already; this form just sends a richer
 * object into the same micrositeUpdateLandingSection contract.
 */
const MicrositeHeroSectionForm = ({ content, onChange }) => {
    const safeContent = withDefaults(content)

    const update = (path, value) => {
        // path is an array of keys, e.g. ['staticConfig', 'theme', 'backgroundColor']
        const next = JSON.parse(JSON.stringify(safeContent))
        let cursor = next
        for (let i = 0; i < path.length - 1; i++) {
            cursor = cursor[path[i]]
        }
        cursor[path[path.length - 1]] = value
        onChange?.(next)
    }

    const { activeMode, commonSettings, staticConfig, sliderConfig } = safeContent

    return (
        <section className={styles.fieldGroup}>
            <h2 className={styles.groupTitle}>Hero banner</h2>
            <p className={styles.groupDesc}>The first thing visitors see when they land on your site.</p>

            <MicrositeTabSwitcher
                value={activeMode}
                onChange={(v) => update(['activeMode'], v)}
                tabs={[
                    { value: 'STATIC', label: 'Static hero' },
                    { value: 'SLIDER', label: 'Slider hero' }
                ]}
            />

            <ToggleSwitch
                label="Hide on mobile"
                description="Skip the hero section entirely on small screens"
                checked={commonSettings.mobileHidden}
                onChange={(v) => update(['commonSettings', 'mobileHidden'], v)}
            />

            {activeMode === 'STATIC' ? (
                <div className={styles.modeBlock}>
                    <h3 className={styles.subTitle}>Layout</h3>
                    <div className={styles.row2}>
                        <SelectInput
                            label="Layout type"
                            value={staticConfig.layoutType}
                            onChange={(v) => update(['staticConfig', 'layoutType'], v)}
                            options={LAYOUT_TYPE_OPTIONS}
                        />
                        <TextInput
                            label="Section height (CSS value)"
                            value={staticConfig.theme.height}
                            onChange={(v) => update(['staticConfig', 'theme', 'height'], v)}
                            placeholder="100vh"
                        />
                    </div>

                    <h3 className={styles.subTitle}>Theme</h3>
                    <div className={styles.row2}>
                        <div className={styles.colorField}>
                            <label className={styles.colorLabel}>Background color</label>
                            <input
                                type="color"
                                className={styles.colorInput}
                                value={staticConfig.theme.backgroundColor}
                                onChange={(e) => update(['staticConfig', 'theme', 'backgroundColor'], e.target.value)}
                            />
                        </div>
                        <div className={styles.colorField}>
                            <label className={styles.colorLabel}>Text color</label>
                            <input
                                type="color"
                                className={styles.colorInput}
                                value={staticConfig.theme.textColor}
                                onChange={(e) => update(['staticConfig', 'theme', 'textColor'], e.target.value)}
                            />
                        </div>
                    </div>

                    <h3 className={styles.subTitle}>Content</h3>
                    <TextInput
                        label="Heading"
                        value={staticConfig.content.heading}
                        onChange={(v) => update(['staticConfig', 'content', 'heading'], v)}
                        placeholder="e.g. Lokaci The Salon Company."
                        maxLength={100}
                        showCharCount
                    />
                    <TextInput
                        label="Subheading"
                        value={staticConfig.content.subHeading}
                        onChange={(v) => update(['staticConfig', 'content', 'subHeading'], v)}
                        placeholder="e.g. Comfort, individual approach to customers."
                        maxLength={150}
                        showCharCount
                    />
                    <TextInput
                        label="Description"
                        value={staticConfig.content.description}
                        onChange={(v) => update(['staticConfig', 'content', 'description'], v)}
                        placeholder="A short supporting line below the subheading"
                        maxLength={220}
                        showCharCount
                    />
                    <SelectInput
                        label="Text alignment"
                        value={staticConfig.content.alignment}
                        onChange={(v) => update(['staticConfig', 'content', 'alignment'], v)}
                        options={ALIGNMENT_OPTIONS}
                    />

                    <label className={styles.rowsLabel}>Buttons / links</label>
                    <ActionListEditor
                        actions={staticConfig.content.actions}
                        onChange={(v) => update(['staticConfig', 'content', 'actions'], v)}
                    />

                    <h3 className={styles.subTitle}>Media</h3>
                    <MicrositeImageUploader
                        label="Image / asset URL"
                        value={staticConfig.media.url}
                        onChange={(v) => update(['staticConfig', 'media', 'url'], v)}
                        placeholder="/assets/hero-image.png"
                        aspectHint="Transparent PNGs work well for SPLIT_SCREEN layouts"
                    />
                    <div className={styles.row2}>
                        <SelectInput
                            label="Media type"
                            value={staticConfig.media.type}
                            onChange={(v) => update(['staticConfig', 'media', 'type'], v)}
                            options={MEDIA_TYPE_OPTIONS}
                        />
                        <SelectInput
                            label="Media position"
                            value={staticConfig.media.position}
                            onChange={(v) => update(['staticConfig', 'media', 'position'], v)}
                            options={MEDIA_POSITION_OPTIONS}
                        />
                    </div>
                    <TextInput
                        label="Alt text"
                        value={staticConfig.media.altText}
                        onChange={(v) => update(['staticConfig', 'media', 'altText'], v)}
                        placeholder="Describe the image for accessibility & SEO"
                    />
                    <ToggleSwitch
                        label="Floating animation"
                        description="Gently animates the media up and down"
                        checked={staticConfig.media.floatingAnimation}
                        onChange={(v) => update(['staticConfig', 'media', 'floatingAnimation'], v)}
                    />

                    <h3 className={styles.subTitle}>Floating widgets</h3>
                    <p className={styles.groupDesc}>Small info cards overlaid on the hero, e.g. "New Arrival".</p>
                    <MicrositeRepeatableRows
                        items={staticConfig.widgets}
                        onChange={(v) => update(['staticConfig', 'widgets'], v)}
                        emptyItem={{ ...EMPTY_WIDGET, id: genId('w') }}
                        addLabel="Add widget"
                        renderSummary={(item) => item.title || 'New widget'}
                        renderItem={(item, idx, updateItem) => (
                            <div className={styles.nestedFields}>
                                <div className={styles.row2}>
                                    <SelectInput
                                        label="Widget type"
                                        value={item.type}
                                        onChange={(v) => updateItem('type', v)}
                                        options={WIDGET_TYPE_OPTIONS}
                                    />
                                    <SelectInput
                                        label="Position"
                                        value={item.position}
                                        onChange={(v) => updateItem('position', v)}
                                        options={WIDGET_POSITION_OPTIONS}
                                    />
                                </div>
                                <div className={styles.row2}>
                                    <TextInput
                                        label="Title"
                                        value={item.title}
                                        onChange={(v) => updateItem('title', v)}
                                        placeholder="e.g. New Arrival"
                                    />
                                    <TextInput
                                        label="Subtitle"
                                        value={item.subtitle}
                                        onChange={(v) => updateItem('subtitle', v)}
                                        placeholder="e.g. Summer Collection"
                                    />
                                </div>
                            </div>
                        )}
                    />
                </div>
            ) : (
                <div className={styles.modeBlock}>
                    <h3 className={styles.subTitle}>Slider settings</h3>
                    <div className={styles.row2}>
                        <ToggleSwitch
                            label="Auto-play"
                            checked={sliderConfig.settings.autoPlay}
                            onChange={(v) => update(['sliderConfig', 'settings', 'autoPlay'], v)}
                        />
                        <TextInput
                            label="Interval (ms)"
                            type="number"
                            value={sliderConfig.settings.interval}
                            onChange={(v) => update(['sliderConfig', 'settings', 'interval'], Number(v) || 0)}
                            placeholder="5000"
                        />
                    </div>
                    <div className={styles.row2}>
                        <ToggleSwitch
                            label="Show arrows"
                            checked={sliderConfig.settings.showArrows}
                            onChange={(v) => update(['sliderConfig', 'settings', 'showArrows'], v)}
                        />
                        <ToggleSwitch
                            label="Show dots"
                            checked={sliderConfig.settings.showDots}
                            onChange={(v) => update(['sliderConfig', 'settings', 'showDots'], v)}
                        />
                    </div>
                    <TextInput
                        label="Section height (CSS value)"
                        value={sliderConfig.settings.height}
                        onChange={(v) => update(['sliderConfig', 'settings', 'height'], v)}
                        placeholder="100vh"
                    />

                    <h3 className={styles.subTitle}>Slides</h3>
                    <MicrositeRepeatableRows
                        items={sliderConfig.slides}
                        onChange={(v) => update(['sliderConfig', 'slides'], v)}
                        emptyItem={{ ...EMPTY_SLIDE, id: genId('slide') }}
                        addLabel="Add slide"
                        renderSummary={(item) => item.content?.heading || 'New slide'}
                        renderItem={(item, idx, updateItem) => {
                            const updateSlideContent = (field, value) =>
                                updateItem('content', { ...item.content, [field]: value })
                            const updateSlideMedia = (field, value) =>
                                updateItem('media', { ...item.media, [field]: value })

                            return (
                                <div className={styles.nestedFields}>
                                    <TextInput
                                        label="Heading"
                                        value={item.content?.heading}
                                        onChange={(v) => updateSlideContent('heading', v)}
                                        placeholder="e.g. The Art of Perfection"
                                    />
                                    <TextInput
                                        label="Subheading"
                                        value={item.content?.subHeading}
                                        onChange={(v) => updateSlideContent('subHeading', v)}
                                        placeholder="e.g. PREMIUM GROOMING EXPERIENCE"
                                    />
                                    <SelectInput
                                        label="Text alignment"
                                        value={item.content?.alignment}
                                        onChange={(v) => updateSlideContent('alignment', v)}
                                        options={ALIGNMENT_OPTIONS}
                                    />

                                    <label className={styles.rowsLabel}>Buttons / links</label>
                                    <ActionListEditor
                                        actions={item.content?.actions}
                                        onChange={(v) => updateSlideContent('actions', v)}
                                    />

                                    <MicrositeImageUploader
                                        label="Background image"
                                        value={item.media?.url}
                                        onChange={(v) => updateSlideMedia('url', v)}
                                        placeholder="https://example.com/slide.jpg"
                                    />
                                    <TextInput
                                        label="Overlay opacity (0–1)"
                                        type="number"
                                        value={item.media?.overlayOpacity}
                                        onChange={(v) => updateSlideMedia('overlayOpacity', Number(v))}
                                        placeholder="0.4"
                                    />
                                </div>
                            )
                        }}
                    />
                </div>
            )}
        </section>
    )
}

export default MicrositeHeroSectionForm